import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listCalculations, deleteCalculation, getCalculation } from "../lib/api";
import { formatRupiah, formatDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import ResultTable from "../components/ResultTable";
import {
  ClockCounterClockwise,
  MagnifyingGlass,
  Trash,
  Eye,
  PencilSimple,
  Printer,
  NoteBlank,
  Plus,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function HistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async (q = "") => {
    setLoading(true);
    try {
      const rows = await listCalculations(q);
      setList(rows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus riwayat ini?")) return;
    await deleteCalculation(id);
    toast.success("Riwayat dihapus.");
    refresh(search);
    if (selected?.id === id) setSelected(null);
  };

  const handleView = (id) => {
    navigate(`/print/${id}`);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white border border-slate-200 rounded shadow-sm p-5 no-print">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Riwayat Perhitungan
            </div>
            <div className="text-xl md:text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <ClockCounterClockwise size={22} weight="duotone" />
              Total: {list.length}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  refresh(e.target.value);
                }}
                placeholder="Cari nama pasien..."
                className="bg-white border border-slate-300 rounded pl-9 pr-3 py-2 text-sm w-64 focus:ring-2 focus:ring-[#0A3F9A]"
                data-testid="input-search-history"
              />
            </div>
            <button
              onClick={() => navigate("/")}
              className="bg-[#0A3F9A] text-white px-4 py-2 rounded hover:bg-[#082F73] text-sm font-medium flex items-center gap-2"
              data-testid="btn-new-calc"
            >
              <Plus size={16} /> Perhitungan Baru
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-10">Memuat...</div>
      ) : list.length === 0 ? (
        <div
          className="bg-white border border-slate-200 rounded shadow-sm p-10 text-center"
          data-testid="empty-history"
        >
          <NoteBlank size={48} weight="duotone" className="mx-auto text-slate-400" />
          <div className="text-lg font-semibold text-slate-900 mt-3">
            Belum ada riwayat perhitungan
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Mulai estimasi biaya operasi dari menu Kalkulator.
          </div>
          <button
            onClick={() => navigate("/")}
            className="mt-5 bg-[#0A3F9A] text-white px-4 py-2 rounded hover:bg-[#082F73] text-sm font-medium inline-flex items-center gap-2"
            data-testid="btn-create-first-calc"
          >
            <Plus size={16} /> Buat Perhitungan Baru
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden no-print">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b">
                  Tanggal
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b">
                  Nama Pasien
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b">
                  Ruang / Kelas
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b">
                  Dokter Operator
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b">
                  Dibuat oleh
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b">
                  Jml Tindakan
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-right p-3 border-b">
                  Grand Total
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-right p-3 border-b">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-slate-50"
                  data-testid={`row-history-${r.id}`}
                >
                  <td className="p-3 border-b border-slate-100 text-slate-700">
                    {formatDate(r.created_at)}
                  </td>
                  <td className="p-3 border-b border-slate-100 font-medium text-slate-900">
                    {r.nama_pasien}
                    {r.source === "AI" && (
                      <span
                        className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-200"
                        title={r.ai_model ? `Disusun dengan ${r.ai_model}` : "Disusun via Mode AI"}
                        data-testid={`badge-ai-${r.id}`}
                      >
                        AI{r.doc_type === "CUSTOM" ? " · Custom" : ""}{r.ai_model ? ` · ${r.ai_model}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="p-3 border-b border-slate-100 text-slate-700">
                    {r.ruang_kelas} · {r.kelas}
                  </td>
                  <td className="p-3 border-b border-slate-100 text-slate-700 text-xs">
                    {r.dokter_operator || "-"}
                  </td>
                  <td
                    className="p-3 border-b border-slate-100 text-slate-700 text-xs"
                    data-testid={`text-created-by-${r.id}`}
                  >
                    {r.created_by || "-"}
                  </td>
                  <td className="p-3 border-b border-slate-100 text-slate-700">
                    {r.result?.tindakan_breakdown?.length || "-"}
                  </td>
                  <td className="p-3 border-b border-slate-100 text-right tabular-nums font-bold text-blue-900">
                    {formatRupiah(r.grand_total)}
                  </td>
                  <td className="p-3 border-b border-slate-100 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleView(r.id)}
                        className="text-slate-600 hover:text-[#0A3F9A] p-1"
                        title="Lihat kop surat"
                        data-testid={`btn-view-${r.id}`}
                      >
                        <Eye size={18} weight="duotone" />
                      </button>
                      {r.doc_type !== "CUSTOM" && (
                        <button
                          onClick={() => navigate(`/?edit=${r.id}`)}
                          className="text-slate-600 hover:text-[#D97706] p-1"
                          title="Panggil & Edit di Kalkulator"
                          data-testid={`btn-edit-${r.id}`}
                        >
                          <PencilSimple size={18} weight="duotone" />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="text-slate-600 hover:text-red-600 p-1"
                          title="Hapus"
                          data-testid={`btn-delete-${r.id}`}
                        >
                          <Trash size={18} weight="duotone" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail view modal/inline */}
      {selected && (
        <div className="bg-white border border-slate-200 rounded shadow-sm p-5 md:p-6 print-area">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4 pb-3 border-b border-slate-100 no-print">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
                Detail Riwayat
              </div>
              <div className="text-xl font-bold text-slate-900">
                {selected.nama_pasien}
              </div>
              <div className="text-sm text-slate-600">
                {selected.ruang_kelas} · {selected.kelas} ·{" "}
                {formatDate(selected.created_at)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.print()}
                className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded text-sm flex items-center gap-2"
                data-testid="btn-print-detail"
              >
                <Printer size={16} weight="duotone" /> Print
              </button>
              <button
                onClick={() => setSelected(null)}
                className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded text-sm flex items-center gap-2"
                data-testid="btn-close-detail"
              >
                <X size={16} /> Tutup
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {selected.result.tindakan_breakdown.map((b, i) => (
              <ResultTable key={i} breakdown={b} index={i} />
            ))}
            <div className="bg-blue-900 text-white rounded p-5 flex items-center justify-between print-card">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-blue-200">
                Grand Total
              </div>
              <div className="text-3xl font-black tabular-nums">
                {formatRupiah(selected.grand_total)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
